import {
  AdjacentStepsMap,
  AdjacentSteps,
  Module,
  PRODUCTION_STEPS_2S,
  PRODUCTION_STEPS_PS,
  StepsAny,
  isStepsAny,
  ComponentInfo,
  SERIAL_NUMBER_REGEXPS,
  ModuleWithUpNext,
  getModuleTypeFromId,
  StepFrontend,
} from "@constants";

/**
 * Wrapper function to check if steps are valid. Used in renderind forms.
 * @param step step name being checked.
 * @returns {boolean} true if step key exists.
 */
export function isValidStep(step: string): step is StepsAny {
  return isStepsAny(step);
}

/**
 * Map wrapper. Returns the adjacent steps for a given input step.
 * @param {string} step input step being checked.
 * @returns {AdjacentSteps} Object corresponding with the input step
 * @throws {Error} if the given step isn't valid.
 */
export function _hasAdjMapNode(step: string) {
  const invalidError = new Error(`${step} is not a valid step`);
  if (!isValidStep(step)) throw invalidError;
  const node = AdjacentStepsMap.get(step);
  if (!node) return false; // This shouldn't trigger.
  return true;
}

/**
 * Map wrapper. Returns the adjacent steps for a given input step.
 * @param {string} step input step being checked.
 * @returns {AdjacentSteps} Object corresponding with the input step
 * @throws {Error} if the given step isn't valid.
 */
export function _getAdjMapNode(step: string): AdjacentSteps {
  const invalidError = new Error(`${step} is not a valid step`);
  if (!isValidStep(step)) throw invalidError;
  const node = AdjacentStepsMap.get(step);
  if (!node) throw invalidError; // This shouldn't trigger.
  return node;
}

/**
 * Returns the next steps for a given step based on the step tree.
 * @param {string} step The step being checked
 * @returns {StepsAny[]}
 */
export function nextSteps(step: string): StepsAny[] {
  return _getAdjMapNode(step).next;
}

/**
 * Returns the next steps for a given step based on the step tree.
 * @param {string} step The step being checked
 * @returns {StepsAny[]}
 */
export function previousSteps(step: string): StepsAny[] {
  return _getAdjMapNode(step).previous;
}

/**
 * Helper function to check if a given step is first step.
 * @param {string} step Step being checked.
 * @returns {boolean} True iff the number of previous steps to this step is zero.
 */
export function isFirstStep(step: string): boolean {
  return _hasAdjMapNode(step) && _getAdjMapNode(step).previous.length === 0;
}

/**
 * Helper function to check if a given step is the final step.
 * @param {string} step Step being checked.
 * @returns {boolean} True iff the number of next steps to this step is zero.
 */
export function isFinalStep(step: string): boolean {
  return _getAdjMapNode(step).next.length === 0;
}

/**
 * Checks to make sure that a step can be completed (has the necessary pre-reqs).
 * @param {Module} module Module being checked
 * @param {string} step step being checked
 * @returns {boolean} true iff all previous steps have been completed.
 */
export function checkModuleForPrerequisites(
  module: Module,
  step: string,
): boolean {
  const previous = previousSteps(step);
  const completedPreviousSteps = module.steps.filter((completedStep) =>
    previous.includes(completedStep),
  );
  return previous.length === completedPreviousSteps.length;
}

/**
 * Checks to make sure that a step can be completed (has the necessary pre-reqs).
 * @param {StepFrontend[]} steps Steps being checked
 * @param {string} step step being checked
 * @returns {boolean} true iff all previous steps have been completed.
 */
export function checkStepsForPrerequisites(
  steps: StepFrontend[],
  step: string,
): boolean {
  const previous = previousSteps(step);
  return (
    previous.length ===
    steps.filter(
      (completedStep) =>
        previous.includes(completedStep.stepName) &&
        completedStep.passed !== false,
    ).length
  );
}

/**
 * Helper function to get the first steps for any module.
 * @param moduleType The type of module
 * @returns {string[]} the array of first steps for the specified module
 */
export function getFirstSteps(moduleType: "2S" | "PS" | string) {
  if (moduleType !== "2S" && moduleType !== "PS") return [];
  const stepsToFilter =
    moduleType === "2S" ? PRODUCTION_STEPS_2S : PRODUCTION_STEPS_PS;
  return Object.values(stepsToFilter).filter((stepName) =>
    isFirstStep(stepName),
  );
}

/**
 * Searches through the total space of steps for a module and returns the next
 * possible steps to be completed.
 * @param moduleType Type of module associated with steps.
 * @param {string[]} steps Steps in a module that have been completed.
 * @returns {string[]} the names of the next possible steps to complete.
 */
export function getNextStepsFromModuleSteps(
  moduleType: "2S" | "PS" | string,
  steps: string[],
): string[] {
  if (!steps) {
    const test = [] as string[];
    return test;
  }
  return steps
    .map((step) => nextSteps(step))
    .reduce((acc, nextStepsForStep) => acc.concat(nextStepsForStep), [])
    .filter((step) => !steps.includes(step));
}

/**
 * Searches through to total space of steps for a module and returns
 * the first step that can be performed next.
 * @param moduleType Type of module associated with steps.
 * @param steps Steps completed by the module so fa.
 * @returns The first step found that needs to be completed.
 */
export function getNextStepFromModuleSteps(
  moduleType: "2S" | "PS" | string,
  steps: string[],
): string {
  const nextStepsForModule = getNextStepsFromModuleSteps(moduleType, steps);
  const nextStepWithPredecessor = nextStepsForModule.find((step) =>
    previousSteps(step).find((prevStep) => steps.includes(prevStep)),
  );
  if (nextStepWithPredecessor) return nextStepWithPredecessor;
  if (nextStepsForModule.length > 0) return nextStepsForModule[0];
  return "";
}

/**
 * Takes a module and computes its next step, returning a ModuleWithUpNext.
 * @param {Module} moduleUpNext Module being computed,
 * @returns {ModuleWithUpNext}
 */
export function getNextStepFromModule(moduleUpNext: Module) {
  return {
    ...moduleUpNext,
    nextStep: getNextStepFromModuleSteps(
      getModuleTypeFromId(moduleUpNext.id),
      moduleUpNext.steps,
    ),
  } as ModuleWithUpNext;
}

/**
 * Computes next step for a collection of modules.
 * @param {Moudle[]} modules array of modules
 * @returns {ModuleWithUpNext[]}
 */
export function getNextStepsForModules(modules: Module[]) {
  return modules.map(getNextStepFromModule);
}

export type { ComponentInfo };

export { SERIAL_NUMBER_REGEXPS };
